"use client";

import { Building2 } from "lucide-react";
import { Fragment, type ReactNode, useEffect } from "react";
import {
  useHash,
  usePathname,
  useRouter,
  useSearchParams,
} from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { CenteredLoading } from "@/components/common/CenteredLoading";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import { DialogHeading } from "@/components/ui/dialog-heading";
import {
  FACILITY_SCOPE_QUERY_PARAM,
  useFacility,
} from "@/contexts/FacilityContext";
import {
  appendFacilityScopeToUrl,
  requiresSingleFacility,
  resolveFacilityScopedPath,
} from "@/lib/facilityModeAccess";

/**
 * 組織全体の表示範囲から単一施設必須ページへ進む際に、更新対象の施設を確定する。
 * メニューを無効化したりダッシュボードへ戻したりせず、選択後は元のURLへ続行する。
 */
export function FacilityModeGuard({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const hash = useHash();
  const searchParams = useSearchParams();
  const { facilities, isLoading, selectedFacilityId, canUseAllFacilities } =
    useFacility();
  const router = useRouter();
  const searchParamsString = searchParams.toString();
  const requestedFacilityScope = searchParams.get(FACILITY_SCOPE_QUERY_PARAM);
  const hasExplicitFacilityScope = requestedFacilityScope !== null;
  const explicitFacilityScopeIsAvailable =
    requestedFacilityScope === "all"
      ? canUseAllFacilities
      : Boolean(
          requestedFacilityScope &&
          facilities.some((facility) => facility.id === requestedFacilityScope)
        );
  const facilityScopeUrlIsResolved =
    !hasExplicitFacilityScope ||
    (explicitFacilityScopeIsAvailable &&
      requestedFacilityScope === selectedFacilityId);
  const isFacilityScopeResolving = Boolean(
    hasExplicitFacilityScope && !facilityScopeUrlIsResolved
  );
  const routeRequiresFacility = requiresSingleFacility(pathname);
  const hasAvailableSelectedFacility = Boolean(
    selectedFacilityId &&
    selectedFacilityId !== "all" &&
    facilities.some((facility) => facility.id === selectedFacilityId)
  );
  const needsFacilitySelection =
    !isLoading && routeRequiresFacility && !hasAvailableSelectedFacility;
  const resolvedSelectedFacilityPath =
    !isLoading &&
    hasAvailableSelectedFacility &&
    hasExplicitFacilityScope &&
    facilityScopeUrlIsResolved &&
    routeRequiresFacility
      ? resolveFacilityScopedPath(pathname, selectedFacilityId)
      : null;
  const needsPathNormalization = Boolean(
    resolvedSelectedFacilityPath && resolvedSelectedFacilityPath !== pathname
  );

  const cancelFacilitySelection = () => {
    const historyIndex =
      typeof window !== "undefined" &&
      typeof window.history.state?.idx === "number"
        ? window.history.state.idx
        : 0;

    if (historyIndex > 0) {
      router.back();
      return;
    }

    router.replace(
      appendFacilityScopeToUrl("/dashboard", selectedFacilityId || "all")
    );
  };

  // 共有URLなどで表示範囲だけが既に単一施設へ復元されている場合も、
  // `/facilities/all/...` を更新対象としてページへ渡さない。
  useEffect(() => {
    if (
      !needsPathNormalization ||
      !resolvedSelectedFacilityPath ||
      !selectedFacilityId
    )
      return;

    const nextSearchParams = new URLSearchParams(searchParamsString);
    nextSearchParams.set(FACILITY_SCOPE_QUERY_PARAM, selectedFacilityId);
    const query = nextSearchParams.toString();
    router.replace(
      `${resolvedSelectedFacilityPath}${query ? `?${query}` : ""}${hash}`
    );
  }, [
    hash,
    needsPathNormalization,
    resolvedSelectedFacilityPath,
    router,
    searchParamsString,
    selectedFacilityId,
  ]);

  const continueWithFacility = (facilityId: string) => {
    // 一覧から渡された実在施設だけを対象とし、`all` を更新対象にしない。
    if (!facilities.some((facility) => facility.id === facilityId)) return;

    const resolvedPath = resolveFacilityScopedPath(pathname, facilityId);
    if (!resolvedPath) return;

    const nextSearchParams = new URLSearchParams(searchParamsString);
    nextSearchParams.set(FACILITY_SCOPE_QUERY_PARAM, facilityId);
    const query = nextSearchParams.toString();
    router.replace(`${resolvedPath}${query ? `?${query}` : ""}${hash}`);
  };

  // 未確定の `all` を実際の組織全体選択として扱わせない。
  // 横断ページも初期化完了までマウントせず、施設限定ユーザーの初回取得や
  // 共有URL復元時に誤った範囲のAPIが走ることを防ぐ。
  if (isLoading) {
    return <CenteredLoading message="施設情報を確認しています…" />;
  }

  // 戻る・進むや施設切替直後も、URLとContextが一致するまで旧ページを外す。
  // 依存配列の不足した画面があっても、旧施設の表示を残さないための最終防衛線。
  if (isFacilityScopeResolving) {
    return <CenteredLoading message="表示する施設を確認しています…" />;
  }

  // 施設URLのqueryがない場合は、FacilityContextがURL内の施設IDを
  // 表示範囲へ反映するまで待つ。保存済みの別施設でパスを書き換えない。
  if (
    routeRequiresFacility &&
    pathname.startsWith("/facilities/") &&
    facilities.length > 0 &&
    !hasExplicitFacilityScope
  ) {
    return <CenteredLoading message="表示する施設を確認しています…" />;
  }

  if (needsPathNormalization) {
    return <CenteredLoading message="表示する施設を切り替えています…" />;
  }

  if (needsFacilitySelection) {
    return (
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) cancelFacilitySelection();
        }}
      >
        <DialogContent showCloseButton={false} className="sm:max-w-md">
          <DialogHeading
            title="操作する施設を選択してください"
            description="この機能は施設ごとに管理されます。施設を選択すると、同じ画面へ進みます。"
          />

          {facilities.length > 0 ? (
            <div
              role="group"
              className="max-h-72 space-y-2 overflow-y-auto py-1"
              aria-label="施設一覧"
            >
              {facilities.map((facility) => (
                <Button
                  key={facility.id}
                  type="button"
                  variant="outline"
                  className="h-auto min-h-11 w-full justify-start gap-3 whitespace-normal px-3 py-2 text-left"
                  onClick={() => continueWithFacility(facility.id)}
                >
                  <Building2 className="size-4 shrink-0" aria-hidden="true" />
                  <span>{facility.name}</span>
                </Button>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground py-4 text-sm">
              選択できる施設がありません。施設の登録状況を確認してください。
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={cancelFacilitySelection}
            >
              キャンセル
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // 施設単位ページでは表示範囲の変更時にページ状態も作り直す。
  // 旧施設の遅延レスポンスや入力値を、新しい施設への更新に流用させない。
  return (
    <Fragment
      key={routeRequiresFacility ? selectedFacilityId : "cross-facility"}
    >
      {children}
    </Fragment>
  );
}
